import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AddCompanyModal({ open, onOpenChange, initialName = "", onCreated }) {
  const [form, setForm] = useState({ name: "", industry: "", location: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ name: initialName, industry: "", location: "" });
      setError("");
    }
  }, [open, initialName]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/companies", form);
      toast.success(`${data.name} added to the directory`);
      onOpenChange(false);
      if (onCreated) onCreated(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-company-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl flex items-center gap-2" style={{ fontWeight: 800 }}>
            <Building2 className="text-emerald-600" /> Add a Company
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-slate-500 -mt-1">
          Can't find your company? Add it here — it's added to the main directory for everyone.
        </DialogDescription>

        {error && (
          <div data-testid="add-company-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
        )}

        <form onSubmit={submit} className="space-y-4 mt-1">
          <div>
            <Label>Company name</Label>
            <Input data-testid="add-company-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Acme Inc." />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Industry (optional)</Label>
              <Input data-testid="add-company-industry-input" value={form.industry} onChange={(e) => set("industry", e.target.value)} placeholder="e.g. Software" />
            </div>
            <div>
              <Label>Location (optional)</Label>
              <Input data-testid="add-company-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Austin, TX" />
            </div>
          </div>
          <Button data-testid="add-company-submit" type="submit" disabled={loading || !form.name.trim()} className="w-full bg-slate-900 hover:bg-emerald-600 rounded-full">
            {loading ? "Adding..." : "Add Company"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
